import { Language } from '@configs/language';

const BASE_LANGUAGE_LABELS = {
  backToLogin: 'Back to Login',
  codeLength: 'Verification code must be 6 digits',
  codeNumeric: 'Verification code must contain only numbers',
  codeRequired: 'Verification code is required',
  confirmPasswordRequired: 'Please confirm your password',
  confirmScenarioDescription:
    'Enter the verification code you received and create a new password.',
  confirmScenarioTitle: 'Confirm New Password',
  emailInvalid: 'Please enter a valid email address',
  emailRequired: 'Email is required',
  errorAuthenticationFailed: 'Authentication failed. Please try again.',
  errorBadRequest: 'Invalid request. Please check your input and try again.',
  errorDefault: 'An error occurred. Please try again.',
  errorExpired:
    'Verification code has expired. Please request a new password reset.',
  errorForbidden:
    'Access denied. You do not have permission to perform this action.',
  errorInvalidCode:
    'Invalid verification code or email. Please check and try again.',
  errorInvalidEmail: 'Please enter a valid email address.',
  errorLoginInvalidCredentials: 'Login failed - Invalid credentials',
  errorNetworkConnection:
    'Network connection error. Please check your internet connection and try again.',
  errorNotFound:
    'Reset request not found. Please request a new password reset.',
  errorServerError: 'Server error. Please try again later.',
  errorServiceUnavailable:
    'Service temporarily unavailable. Please try again later.',
  errorTooManyRequests: 'Too many reset requests. Please try again later.',
  errorUnauthorized: 'Authentication required. Please log in again.',
  errorUnexpectedLogin: 'An unexpected error occurred during login',
  forgotPasswordDescription:
    "Enter your email address and we'll send you a link to reset your password.",
  forgotPasswordEmailLabel: 'Email Address',
  forgotPasswordEmailPlaceholder: 'Enter your email address',
  forgotPasswordSubmitButton: 'Send Reset Link',
  forgotPasswordSubmittingButton: 'Sending...',
  forgotPasswordSuccessDescription:
    'Check your email for further instructions. Redirecting...',
  forgotPasswordSuccessMessage:
    'Password reset instructions have been sent to your email.',
  forgotPasswordTitle: 'Reset Password',
  passwordExpired: 'Your password has expired',
  passwordExpiredDescription:
    'For security reasons, we need you to update your password. Click the button below to create a new password securely.',
  loginSuccess: 'Login Success',
  migrationScenarioDescription:
    'Your password has expired. Please enter a new secure password.',
  migrationScenarioTitle: 'Update Your Password',
  passwordRequired: 'Password is required',
  passwordsMustMatch: 'Passwords do not match',
  passwordUpdateSuccessMessage:
    'Password updated successfully. Please login with your new password.',
  resetLinkSent: 'Link sent',
  resetPasswordButton: 'Reset Password',
  resetPasswordCodeAutoFilled: 'Code auto-filled from reset link',
  resetPasswordCodeHelper: 'Check your email for the verification code',
  resetPasswordCodeLabel: 'Verification Code',
  resetPasswordCodePlaceholder: 'Enter 6-digit code',
  resetPasswordConfirmPasswordLabel: 'Confirm New Password',
  resetPasswordDescription:
    'Enter the verification code sent to your email and create a new password.',
  resetPasswordDescriptionLegacy: 'Create a new password.',
  resetPasswordEmailAutoFilled: 'Email auto-filled from reset link',
  resetPasswordEmailLabel: 'Email Address',
  resetPasswordPasswordLabel: 'New Password',
  resetPasswordSubmitButton: 'Reset Password',
  resetPasswordSubmittingButton: 'Resetting Password...',
  resetPasswordSuccessMessage:
    'Password has been reset successfully! Redirecting to login...',
  resetPasswordSuccessRedirectMessage:
    'Password reset successful. Please login with your new password.',
  resetPasswordTitle: 'Reset Your Password',
  resetScenarioDescription:
    'Complete the process with the verification code sent to your email.',
  resetScenarioTitle: 'Complete Password Reset',
  sendingResetLink: 'Sending...',
  errorUserNotFound: 'No account found with this email address.',
};

type LabelsLanguage = keyof typeof BASE_LANGUAGE_LABELS;

export type AuthTranslationKey = LabelsLanguage;

const AUTH_LABELS: Record<Language, Record<LabelsLanguage, string>> = {
  EN: BASE_LANGUAGE_LABELS,
  ES: {
    backToLogin: 'Volver al Login',
    codeLength: 'El código de verificación debe tener 6 dígitos',
    codeNumeric: 'El código de verificación debe contener solo números',
    codeRequired: 'El código de verificación es requerido',
    confirmPasswordRequired: 'Por favor confirma tu contraseña',
    confirmScenarioDescription:
      'Ingresa el código de verificación recibido y crea una nueva contraseña.',
    confirmScenarioTitle: 'Confirmar Nueva Contraseña',
    emailInvalid: 'Por favor ingresa un email válido',
    emailRequired: 'El email es requerido',
    errorAuthenticationFailed:
      'Falló la autenticación. Por favor intenta de nuevo.',
    errorBadRequest:
      'Solicitud inválida. Por favor verifica tu información e intenta de nuevo.',
    errorDefault: 'Ocurrió un error. Por favor intenta de nuevo.',
    errorExpired:
      'El código de verificación ha expirado. Por favor solicita un nuevo restablecimiento de contraseña.',
    errorForbidden:
      'Acceso denegado. No tienes permisos para realizar esta acción.',
    errorInvalidCode:
      'Código de verificación o email inválido. Por favor verifica e intenta de nuevo.',
    errorInvalidEmail: 'Por favor ingresa un email válido.',
    errorLoginInvalidCredentials:
      'Error de inicio de sesión - Credenciales inválidas',
    errorNetworkConnection:
      'Error de conexión de red. Por favor verifica tu conexión a internet e intenta de nuevo.',
    errorNotFound:
      'Solicitud de restablecimiento no encontrada. Por favor solicita un nuevo restablecimiento de contraseña.',
    errorServerError: 'Error del servidor. Por favor intenta más tarde.',
    errorServiceUnavailable:
      'Servicio temporalmente no disponible. Por favor intenta más tarde.',
    errorTooManyRequests:
      'Demasiadas solicitudes. Por favor intenta más tarde.',
    errorUnauthorized:
      'Autenticación requerida. Por favor inicia sesión de nuevo.',
    errorUnexpectedLogin:
      'Ocurrió un error inesperado durante el inicio de sesión',
    forgotPasswordDescription:
      'Ingresa tu dirección de email y te enviaremos un enlace para restablecer tu contraseña.',
    forgotPasswordEmailLabel: 'Dirección de Email',
    forgotPasswordEmailPlaceholder: 'Ingresa tu dirección de email',
    forgotPasswordSubmitButton: 'Enviar Enlace',
    forgotPasswordSubmittingButton: 'Enviando...',
    forgotPasswordSuccessDescription:
      'Revisa tu email para más instrucciones. Redirigiendo...',
    forgotPasswordSuccessMessage:
      'Las instrucciones para restablecer tu contraseña han sido enviadas a tu email.',
    forgotPasswordTitle: 'Restablece Contraseña',
    passwordExpired: 'Tu contraseña ha expirado',
    passwordExpiredDescription:
      'Por seguridad, necesitamos que actualices tu contraseña. Haz clic en el botón de abajo para crear una nueva contraseña de forma segura.',
    loginSuccess: 'Inicio de sesión exitoso',
    migrationScenarioDescription:
      'Tu contraseña ha expirado. Por favor ingresa una nueva contraseña segura.',
    migrationScenarioTitle: 'Actualizar Tu Contraseña',
    passwordRequired: 'La contraseña es requerida',
    passwordsMustMatch: 'Las contraseñas no coinciden',
    passwordUpdateSuccessMessage:
      'Contraseña actualizada exitosamente. Por favor inicia sesión con tu nueva contraseña.',
    resetLinkSent: 'Enlace enviado',
    resetPasswordButton: 'Restablecer contraseña',
    resetPasswordCodeAutoFilled:
      'Código auto-completado desde el enlace de restablecimiento',
    resetPasswordCodeHelper:
      'Revisa tu email para obtener el código de verificación',
    resetPasswordCodeLabel: 'Código de Verificación',
    resetPasswordCodePlaceholder: 'Ingresa código de 6 dígitos',
    resetPasswordConfirmPasswordLabel: 'Confirmar Contraseña',
    resetPasswordDescription:
      'Ingresa el código de verificación enviado a tu email y crea una nueva contraseña.',
    resetPasswordDescriptionLegacy: 'Ingresa una nueva contraseña.',
    resetPasswordEmailAutoFilled:
      'Email auto-completado desde el enlace de restablecimiento',
    resetPasswordEmailLabel: 'Dirección de Email',
    resetPasswordPasswordLabel: 'Contraseña',
    resetPasswordSubmitButton: 'Restablecer Contraseña',
    resetPasswordSubmittingButton: 'Restableciendo Contraseña...',
    resetPasswordSuccessMessage:
      '¡La contraseña ha sido restablecida exitosamente! Redirigiendo al login...',
    resetPasswordSuccessRedirectMessage:
      'Contraseña restablecida exitosamente. Por favor inicia sesión con tu nueva contraseña.',
    resetPasswordTitle: 'Restablecer Tu Contraseña',
    resetScenarioDescription:
      'Completa el proceso con el código de verificación enviado a tu email.',
    resetScenarioTitle: 'Completar Restablecimiento',
    sendingResetLink: 'Enviando...',
    errorUserNotFound: 'No se encontró una cuenta con este email.',
  },
};

export default AUTH_LABELS;
